import type { IOverviewResponse } from '@candyland/generic/tt-com-shared-overviews';
import { createReducer, on } from '@ngrx/store';
import {
  loadOverview,
  loadOverviewFailure,
  loadOverviewSuccess,
  selectOverview,
  unselectOverview,
} from './overviews.actions';
import type { EntityAdapter, EntityState } from '@ngrx/entity';
import { createEntityAdapter } from '@ngrx/entity';

export const OVERVIEWS_FEATURE_KEY = 'overviews';

export interface OverviewEntity {
  storeId: string;
  page: string;
  status: 'Loading' | 'Loaded' | 'Failed';
  overview: IOverviewResponse;
  error: { statusCode: number; message: string };
  fetchDate?: Date;
}

export interface OverviewState extends EntityState<OverviewEntity> {
  currentOverviewId: string;
}

export interface OverviewPartialState {
  [OVERVIEWS_FEATURE_KEY]: OverviewState;
}

export const overviewsAdapter: EntityAdapter<OverviewEntity> = createEntityAdapter({
  selectId: (model) => model.storeId,
});

export const initialState: OverviewState = overviewsAdapter.getInitialState({
  currentOverviewId: null,
});

const _overviewReducer = createReducer(
  initialState,

  on(unselectOverview, (state) => ({ ...state, currentOverviewId: null })),

  on(selectOverview, (state, { storeId }) => ({ ...state, currentOverviewId: storeId })),

  on(loadOverview, (state, { storeId }) => {
    const entity = {
      storeId,
      status: 'Loading',
    } as OverviewEntity;

    return overviewsAdapter.upsertOne(entity, state);
  }),

  on(loadOverviewSuccess, (state, { storeId, page, overview, fetchDate }) =>
    overviewsAdapter.upsertOne(
      {
        storeId,
        page: page,
        status: 'Loaded',
        overview: overview,
        error: null,
        fetchDate: fetchDate,
      },
      state
    )
  ),

  on(loadOverviewFailure, (state, { storeId, overviewId, overviewType, page, message, statusCode }) =>
    overviewsAdapter.upsertOne(
      {
        storeId,
        status: 'Failed',
        page: page,
        overview: { id: overviewId, type: overviewType, version: null, meta: null, data: null },
        error: {
          statusCode,
          message,
        },
      },
      { ...state }
    )
  )
);

export function overviewReducer(state, action) {
  return _overviewReducer(state, action);
}
